/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import {
  LegendContextMenuContext,
  SectionalLegendContextMenuContext,
  ItemInfo,
  LegendSectionData
} from '#utils/UNSAFE_visTypes/legend';
import { type gestureTypes, useContextMenu } from '#hooks/PRIVATE_useVisContextMenu';
import { getItemInfo } from '#utils/PRIVATE_legendUtils/legendUtils';
import { ContextMenuConfig } from '#utils/UNSAFE_visTypes/common';

type BaseProps = {
  isReadOnly: boolean;
  rootRef: RefObject<HTMLDivElement>;
  focusedItemInfo: ItemInfo;
  onContextMenuDismissed?: (gesture: gestureTypes) => void;
};

type LegendUseContextMenuProps<D> = BaseProps & {
  items: D[];
  contextMenuConfig?: ContextMenuConfig<LegendContextMenuContext<D>>;
};

type SectionalLegendUseContextMenuProps<D, S> = BaseProps & {
  sections: S[];
  contextMenuConfig?: ContextMenuConfig<SectionalLegendContextMenuContext<D>>;
};

export const useLegendContextMenu = <D>({
  rootRef,
  contextMenuConfig,
  focusedItemInfo,
  items,
  isReadOnly,
  onContextMenuDismissed
}: LegendUseContextMenuProps<D>) => {
  const { contextMenuProps, contextMenuContent } = useContextMenu<LegendContextMenuContext<D>>({
    onContextMenuHandler: ({ gesture, target }) => {
      if (gesture === 'keyboard') {
        const itemIdx = focusedItemInfo.itemIdx;
        const elem = rootRef.current?.querySelector(`[data-oj-private-item="${itemIdx}"]`) || null;
        const data = items[itemIdx];
        const context = { type: 'item' as const, data };
        return {
          type: 'element',
          context,
          elem
        };
      }
      const itemIdx = getItemInfo(target as HTMLElement)?.itemIdx;
      const data = itemIdx !== undefined ? items[itemIdx] : null;
      const context = data ? { type: 'item' as const, data } : { type: 'background' as const };
      return {
        type: 'pointer',
        context
      };
    },
    contextMenuOptions: {
      isDisabled: !contextMenuConfig || isReadOnly //If there is no context menu renderer or if isReadOnly is true, we disable the hook
    },
    rootRef,
    contextMenuConfig,
    onContextMenuDismissed
  });

  return { contextMenuContent, contextMenuProps };
};

export const useSectionalLegendContextMenu = <K, D, S extends LegendSectionData<K, D>>({
  rootRef,
  contextMenuConfig,
  focusedItemInfo,
  sections,
  isReadOnly,
  onContextMenuDismissed
}: SectionalLegendUseContextMenuProps<D, S>) => {
  const { contextMenuProps, contextMenuContent } = useContextMenu<
    SectionalLegendContextMenuContext<D>
  >({
    onContextMenuHandler: ({ gesture, target }) => {
      if (gesture === 'keyboard') {
        const itemIdx = focusedItemInfo.itemIdx;
        const sectionIdx = focusedItemInfo.sectionIdx;
        const data = sections[sectionIdx]?.items[itemIdx];
        if (data) {
          const elem =
            rootRef.current?.querySelector(
              `[data-oj-private-item="${itemIdx}"][data-oj-private-section="${sectionIdx}"]`
            ) || null;
          const context = { type: 'item' as const, itemIndexPath: [sectionIdx, itemIdx], data };
          return {
            type: 'element',
            context,
            elem
          };
        }
      }
      const itemIdx = getItemInfo(target as HTMLElement)?.itemIdx;
      const sectionIdx = getItemInfo(target as HTMLElement)?.sectionIdx;
      const data =
        sectionIdx !== undefined && itemIdx !== undefined
          ? sections[sectionIdx]?.items[itemIdx]
          : null;
      const itemIndexPath = [sectionIdx!, itemIdx!];
      const context = data
        ? { type: 'item' as const, data, itemIndexPath }
        : { type: 'background' as const };
      return {
        type: 'pointer',
        context
      };
    },
    contextMenuOptions: {
      isDisabled: !contextMenuConfig || isReadOnly //If there is no context menu renderer or if isReadOnly is true, we disable the hook
    },
    rootRef,
    contextMenuConfig,
    onContextMenuDismissed
  });

  return { contextMenuContent, contextMenuProps };
};
